import { get, isArray, isString } from 'lodash';

/**
 * Normalizes a block `options` prop to always return an array.
 *
 * BlockNote persists prop values as DOM attributes when a structure round-trips
 * through HTML, which coerces arrays and objects to strings. Forms authored in
 * `modules/form` can therefore arrive with `options` as a serialized string
 * rather than an array. Mirrors `parseBlockOptions` in `@packages/form`.
 */
export const parseOptions = <T>(options: T[] | string | null | undefined): T[] => {
  if (!options) return [];
  if (isArray(options)) return options;
  try {
    const parsed: unknown = JSON.parse(options as string);
    return isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
};

/**
 * Resolves the select field type id from either persisted shape.
 *
 * Forms authored in the v7 builder store an object (`{ id, label, value }`);
 * forms authored in `modules/form` store the numeric id as a string, because
 * `selectBlockSchema` types the prop as `z.string().nullable()` and
 * `frm_publish_form` validates it as a non-nullable `String!`.
 *
 * @returns the numeric id, or `null` when the field is not API-driven
 */
export const resolveFieldTypeId = (selectFieldType: unknown): number | null => {
  const raw: unknown = get(selectFieldType, 'id', selectFieldType);
  if (raw === null || raw === undefined || raw === '') return null;
  const id = Number(raw);
  return Number.isFinite(id) ? id : null;
};

/**
 * Resolves the parent field label from either persisted shape.
 *
 * v7-authored forms store an object (`{ label, value }`); `modules/form`
 * stores the parent block's `attrkey` as a string. The label is used to look
 * up the parent's selected value in form data.
 *
 * @returns the parent label, or `null` when no parent is configured
 */
export const resolveParentLabel = (selectParentField: unknown): string | null => {
  const raw: unknown = get(selectParentField, 'label', selectParentField);
  return isString(raw) && raw !== '' ? raw : null;
};
